import { useCallback, useMemo } from 'react';
import { RackingArea } from '@thive/fddm-types';

import { SelectInput } from '@common/components/inputs';
import { useReadSelectedAreas } from '@/modules/floorplanDesign/hooks';
import { areasToFlatCollection, isRackingArea } from '@/modules/floorplanDesign/helpers';
import { FddmServiceFactory } from '@/modules/canvas/services/Floorplan/FddmServiceFactory';

const OPTIONS: { label: string; value: RackingArea['connectNeighbors'] }[] = [
  { label: 'No', value: 'NO_NEIGHBOR_MAPPING' },
  { label: 'Curved (forward only)', value: 'MAP_NEIGHBORS_WITHOUT_PIVOTS_FORWARD_ONLY' },
  { label: 'Curved (back and forth)', value: 'MAP_NEIGHBORS_WITHOUT_PIVOTS_BACK_AND_FORTH' },
  { label: 'Pivot (forward only)', value: 'MAP_NEIGHBORS_WITH_PIVOTS_FORWARD_ONLY' },
  { label: 'Pivot (back and forth)', value: 'MAP_NEIGHBORS_WITH_PIVOTS_BACK_AND_FORTH' },
];

export function RackingConnectNeighbors() {
  const selectedAreas = useReadSelectedAreas();
  const fddmSvc = useMemo(() => FddmServiceFactory.getInstance().createFddmService(), []);

  const connectNeighbors = useMemo(() => {
    const areas = areasToFlatCollection(selectedAreas);
    if (areas.length !== 1) return;
    const area = areas[0];
    if (!isRackingArea(area)) return;
    return area.connectNeighbors;
  }, [selectedAreas]);

  const onChange = useCallback(
    (newValue: RackingArea['connectNeighbors']) => {
      const areas = areasToFlatCollection(selectedAreas);
      if (areas.length !== 1) return;
      const area = areas[0];
      if (!isRackingArea(area)) return;
      fddmSvc.updateArea({ ...area, connectNeighbors: newValue });
      fddmSvc.setPreference('rackingArea.connectNeighbors', newValue);
    },
    [selectedAreas, fddmSvc],
  );

  if (connectNeighbors === undefined) return null;

  return <SelectInput options={OPTIONS} onChange={onChange} value={connectNeighbors} />;
}
